"use client"

import * as React from "react"
import { Check, ChevronsUpDown, GripVertical, Search, X } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface OrderedMultiSelectOption {
  value: string
  label: string
  description?: string
}

interface OrderedMultiSelectProps {
  options: OrderedMultiSelectOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  maxHeight?: string
}

// Sortable badge component
function SortableBadge({
  id,
  label,
  onRemove,
  disabled,
}: {
  id: string
  label: string
  onRemove: (e: React.MouseEvent) => void
  disabled?: boolean
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  }

  return (
    <div ref={setNodeRef} style={style} className="inline-flex">
      <Badge
        variant="secondary"
        className={cn(
          "text-xs shrink-0 cursor-grab active:cursor-grabbing select-none",
          isDragging && "opacity-80 shadow-lg"
        )}
      >
        <GripVertical
          className="h-3 w-3 mr-0.5 text-muted-foreground"
          {...attributes}
          {...listeners}
        />
        <span className="truncate max-w-[100px]">{label}</span>
        <div
          className="ml-1 h-3 w-3 p-0 cursor-pointer rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
          onClick={onRemove}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <X className="h-2 w-2" />
        </div>
      </Badge>
    </div>
  )
}

export function OrderedMultiSelect({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyText = "No options found.",
  disabled = false,
  className,
  maxHeight = "300px",
}: OrderedMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Get selected options in order
  const selectedOptions = React.useMemo(() => {
    return value
      .map((v) => options.find((opt) => opt.value === v))
      .filter((opt): opt is OrderedMultiSelectOption => opt !== undefined)
  }, [value, options])

  const filteredOptions = React.useMemo(() => {
    if (!search) return options

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(search.toLowerCase()) ||
        option.value.toLowerCase().includes(search.toLowerCase()) ||
        option.description?.toLowerCase().includes(search.toLowerCase())
    )
  }, [options, search])

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue]
    onValueChange(newValue)
    setSearch("")
  }

  const handleRemoveOption = (optionValue: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const newValue = value.filter((v) => v !== optionValue)
    onValueChange(newValue)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = value.indexOf(active.id as string)
      const newIndex = value.indexOf(over.id as string)
      const newValue = arrayMove(value, oldIndex, newIndex)
      onValueChange(newValue)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearch("")
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between min-h-[40px] h-auto pr-3",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center w-full min-w-0">
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 mr-2">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground truncate">
                  {placeholder}
                </span>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={value}
                    strategy={horizontalListSortingStrategy}
                  >
                    {selectedOptions.map((option) => (
                      <SortableBadge
                        key={option.value}
                        id={option.value}
                        label={option.label}
                        onRemove={(e) => handleRemoveOption(option.value, e)}
                        disabled={disabled}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-full z-[100]"
        align="start"
        side="bottom"
        sideOffset={4}
        style={{
          width: "var(--radix-popover-trigger-width)",
          maxWidth: "var(--radix-popover-trigger-width)",
        }}
      >
        <div className="border-b px-3 py-2">
          <div className="flex items-center">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            />
          </div>
        </div>
        <div
          className="overflow-y-auto overscroll-contain"
          style={{
            maxHeight: maxHeight,
            scrollbarWidth: "thin",
            scrollbarColor: "rgb(203 213 225) transparent",
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value)
                const selectionIndex = value.indexOf(option.value)
                return (
                  <div
                    key={option.value}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                      isSelected && "bg-accent/50"
                    )}
                    onClick={() => handleToggleOption(option.value)}
                  >
                    <div className="w-5 mr-2 flex items-center justify-center">
                      {isSelected ? (
                        <span className="text-xs font-medium text-primary">
                          {selectionIndex + 1}
                        </span>
                      ) : (
                        <Check className="h-4 w-4 opacity-0" />
                      )}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate">{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground truncate">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
